// src/pages/GoldSchemeEnrollmentForm.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { FaArrowLeft } from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Select from "react-select";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";

import {
  useGetAllGoldSchemesQuery,
  useAddGoldSchemeEnrollmentMutation,
  useGetGoldSchemeEnrollmentByIdQuery,
  useUpdateGoldSchemeEnrollmentMutation,
} from "../toolkitServices/goldSchemeApi";
import { useGetAllCustomersQuery } from "../toolkitServices/customerApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import QuickAddCustomerModal from "../components/other/QuickAddCustomerModal";

// ---------- Shared UI tokens ----------
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none bg-white";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

// ✅ Defaults
const DEFAULTS = {
  branchId: "",
  schemeId: "",
  customerId: "",
  startDate: new Date().toISOString().slice(0, 10),
  schemeAmountPerMonth: "",
  enrollmentCode: "",
  remarks: "",
};

const STEP_FIELDS = {
  1: ["branchId", "schemeId", "customerId"],
  2: ["startDate", "schemeAmountPerMonth", "enrollmentCode", "remarks"],
};

// ---------- react-select styles ----------
const rsStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    height: 44,
    borderRadius: 12,
    borderColor: state.isFocused ? "#9481ce" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(148,129,206,.20)" : "none",
    "&:hover": { borderColor: "#9481ce" },
    fontSize: "14px",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 10px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 44,
  }),
  menu: (base) => ({ ...base, zIndex: 60 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const withErrBorder = (hasErr) => {
  if (!hasErr) return rsStyles;
  return {
    ...rsStyles,
    control: (base, state) => ({
      ...rsStyles.control(base, state),
      borderColor: "#ef4444",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(239,68,68,.20)" : "none",
      "&:hover": { borderColor: "#ef4444" },
    }),
  };
};

const addMonths = (date, months) => {
  if (!date || !months) return null;
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + Number(months));
  if (d.getDate() < day) d.setDate(0);
  return d;
};

const GoldSchemeEnrollmentForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const authUser = useSelector((s) => s.auth?.user);

  const [step, setStep] = useState(1);
  const [quickCustomerOpen, setQuickCustomerOpen] = useState(false);
  const [quickAddedCustomer, setQuickAddedCustomer] = useState(null);
  const hydratedIdRef = useRef(null);
  const customerSelectRef = useRef(null);

  const [addEnrollment, addState] = useAddGoldSchemeEnrollmentMutation();
  const [updateEnrollment, updateState] =
    useUpdateGoldSchemeEnrollmentMutation();

  // Branch list
  const {
    data: branchesRes,
    isLoading: branchesLoading,
    isFetching: branchesFetching,
    error: branchesError,
  } = useGetAllBranchesQuery({
    page: 1,
    limit: 1000,
    search: "",
    active: true,
  });

  const branches = useMemo(() => {
    const raw = branchesRes?.data || branchesRes?.items || branchesRes;
    return Array.isArray(raw) ? raw : [];
  }, [branchesRes]);

  // Customers
  const {
    data: customersRes,
    isLoading: customersLoading,
    isFetching: customersFetching,
    error: customersError,
    refetch: refetchCustomers,
  } = useGetAllCustomersQuery();

  const customers = useMemo(() => {
    const raw = customersRes?.data || customersRes?.items || customersRes;
    return Array.isArray(raw) ? raw : [];
  }, [customersRes]);

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    setValue,
    watch,
    trigger,
    getFieldState,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: DEFAULTS,
    mode: "onBlur",
    shouldUnregister: false,
  });

  const branchId = watch("branchId");
  const schemeId = watch("schemeId");
  const startDate = watch("startDate");

  // Enrollment detail in edit mode
  const {
    data: enrollmentRes,
    isLoading: enrollmentLoading,
    isFetching: enrollmentFetching,
    error: enrollmentError,
  } = useGetGoldSchemeEnrollmentByIdQuery(
    isEdit
      ? {
          id,
          branchId: authUser?.branchId,
          tenantId: authUser?.tenantId,
        }
      : skipToken
  );

  // ✅ branch for scheme list (important fix for edit)
  const branchForSchemeList =
    (isEdit &&
      (enrollmentRes?.data?.branchId || enrollmentRes?.data?.branch?._id)) ||
    branchId ||
    "";

  // Schemes (branch-wise)
  const {
    data: schemesRes,
    isLoading: schemesLoading,
    isFetching: schemesFetching,
    error: schemesError,
  } = useGetAllGoldSchemesQuery(
    authUser?.tenantId
      ? {
          page: 1,
          limit: 1000,
          isActive: true,
          tenantId: authUser.tenantId,
          branchId: branchForSchemeList || undefined,
        }
      : skipToken
  );

  const schemes = useMemo(() => {
    const raw = schemesRes?.data || schemesRes?.items || schemesRes;
    return Array.isArray(raw) ? raw : [];
  }, [schemesRes]);

  // Options
  const branchOptions = useMemo(
    () =>
      branches.map((b) => ({
        value: b._id || b.id || b.branchId,
        label: b.branchName || b.name || "Unnamed Branch",
        raw: b,
      })),
    [branches]
  );

  const schemeOptions = useMemo(
    () =>
      schemes.map((s) => ({
        value: s._id || s.id,
        label: `${s.schemeName || "Scheme"}${
          s.schemeType ? ` (${s.schemeType})` : ""
        }${s.schemeCode ? ` • ${s.schemeCode}` : ""}`,
        raw: s,
      })),
    [schemes]
  );

 const filteredCustomers = useMemo(() => {
  if (!branchForSchemeList) return [];
  const matching = customers.filter(
    (c) => String(c.branchId || c.branch?._id || "") === String(branchForSchemeList)
  );
  if (
    quickAddedCustomer &&
    String(quickAddedCustomer.branchId || quickAddedCustomer.branch?._id || "") ===
      String(branchForSchemeList) &&
    !matching.some(
      (customer) =>
        String(customer._id || customer.id) ===
        String(quickAddedCustomer._id || quickAddedCustomer.id),
    )
  ) {
    return [quickAddedCustomer, ...matching];
  }
  return matching;
}, [customers, branchForSchemeList, quickAddedCustomer]);

const customerOptions = useMemo(
  () =>
    filteredCustomers.map((c) => ({
      value: c._id || c.id,
      label:
        c.customerName ||
        c.name ||
        c.customerId ||
        c.customerCode ||
        c.contact ||
        c.mobile ||
        c.phone ||
        "Unnamed",
      raw: c,
    })),
  [filteredCustomers]
);




  const selectedScheme = useMemo(() => {
    const opt = schemeOptions.find((o) => String(o.value) === String(schemeId));
    return opt?.raw || null;
  }, [schemeOptions, schemeId]);

  const maturityMonths = useMemo(() => {
    if (!selectedScheme) return "";
    const inst = Number(selectedScheme.installmentMonths || 0);
    const ben =
      selectedScheme.schemeType === "AMOUNT"
        ? Number(selectedScheme.benefitsInMonths || 0)
        : 0;
    const total = inst + ben;
    return total ? String(total) : "";
  }, [selectedScheme]);

  const maturityPreviewDate = useMemo(() => {
    if (!startDate || !maturityMonths) return null;
    return addMonths(startDate, Number(maturityMonths));
  }, [startDate, maturityMonths]);

  // Focus on mount
  useEffect(() => {
    const rid = requestAnimationFrame(() => setFocus("branchId"));
    return () => cancelAnimationFrame(rid);
  }, [setFocus]);

  // Toasts
  useEffect(() => {
    if (branchesError) toast.error("Failed to load branches");
  }, [branchesError]);

  useEffect(() => {
    if (customersError) toast.error("Failed to load customers");
  }, [customersError]);

  useEffect(() => {
    if (schemesError) toast.error("Failed to load schemes");
  }, [schemesError]);

  useEffect(() => {
    if (enrollmentError) toast.error("Failed to load enrollment details");
  }, [enrollmentError]);

  // Hydrate in edit mode
  useEffect(() => {
    if (!isEdit) return;
    const e = enrollmentRes?.data;
    if (!e || !id) return;
    if (hydratedIdRef.current === id) return;

    const patch = {
      ...DEFAULTS,
      branchId: e.branchId || e.branch?._id || authUser?.branchId || "",
      schemeId: e.schemeId || e.scheme?._id || "",
      customerId: e.customerId || e.customer?._id || "",
      startDate: e.startDate
        ? new Date(e.startDate).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      schemeAmountPerMonth:
        e.schemeAmountPerMonth != null ? String(e.schemeAmountPerMonth) : "",
      remarks: e.remarks || e.note || "",
      enrollmentCode: e.enrollmentCode || e.code || "",
    };

    reset(patch);
    hydratedIdRef.current = id;
  }, [isEdit, id, enrollmentRes, reset, authUser?.branchId]);

  // ✅ When scheme changes: set default only if field not dirty / empty
  useEffect(() => {
    if (!selectedScheme) return;

    const field = getFieldState("schemeAmountPerMonth");
    const currentVal = watch("schemeAmountPerMonth");

    // if user already typed something (dirty), don't overwrite
    if (field?.isDirty && currentVal !== "" && currentVal != null) return;

    if (selectedScheme.schemeAmountPerMonth != null) {
      setValue(
        "schemeAmountPerMonth",
        String(selectedScheme.schemeAmountPerMonth),
        {
          shouldDirty: false,
        }
      );
    } else {
      setValue("schemeAmountPerMonth", "", { shouldDirty: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScheme?._id]);

  // Step focus
  useEffect(() => {
    const rid = requestAnimationFrame(() => {
      if (step === 1) setFocus("branchId");
      if (step === 2) setFocus("startDate");
    });
    return () => cancelAnimationFrame(rid);
  }, [step, setFocus]);

  // Step navigation
  const nextStep = useCallback(async () => {
    const ok = await trigger(STEP_FIELDS[step], { shouldFocus: true });
    if (!ok) return;
    setStep((s) => (s < 2 ? s + 1 : s));
  }, [step, trigger]);

  const prevStep = useCallback(() => {
    setStep((s) => (s > 1 ? s - 1 : s));
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (e) => {
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
      const isBack = (e.ctrlKey || e.metaKey) && e.key === "Backspace";
      const isPrev = e.altKey && e.key === "ArrowLeft";

      if (isSave) {
        e.preventDefault();
        if (step === 2) handleSubmit(onSubmit)();
        else nextStep();
      }
      if (isBack) {
        e.preventDefault();
        navigate(-1);
      }
      if (isPrev) {
        e.preventDefault();
        prevStep();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [step, handleSubmit, nextStep, prevStep, navigate]);

  // ✅ Validation: min amount rule
  const amountValidate = useCallback(
    (value) => {
      if (!selectedScheme) return "Select scheme first.";
      const v = Number(value || 0);

      if (value === "" || value == null) return "Amount per month is required.";
      if (v <= 0) return "Amount per month must be > 0.";

      const min = Number(selectedScheme.schemeAmountPerMonth || 0);
      if (min > 0 && v < min) return `Amount must be ≥ ${min.toFixed(2)}`;

      return true;
    },
    [selectedScheme]
  );

  // Submit
  const onSubmit = async (form) => {
    if (!authUser?.tenantId) {
      toast.error("Tenant not found. Please re-login.");
      return;
    }

    const payload = {
      schemeId: form.schemeId,
      customerId: form.customerId,
      startDate: form.startDate,

      schemeAmountPerMonth:
        form.schemeAmountPerMonth !== "" && form.schemeAmountPerMonth != null
          ? Number(form.schemeAmountPerMonth)
          : undefined,

      remarks: (form.remarks || "").trim() || undefined,
      enrollmentCode: (form.enrollmentCode || "").trim() || undefined,

      tenantId: authUser.tenantId,
      branchId: form.branchId || authUser?.branchId,
    };

    try {
      if (isEdit) {
        await updateEnrollment({ id, ...payload }).unwrap();
        toast.success("Enrollment updated successfully.");
      } else {
        await addEnrollment(payload).unwrap();
        toast.success("Enrollment created successfully.");
      }
      navigate("/gold-schemes/enrollments");
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.data?.error ||
        err?.error ||
        "Failed to save enrollment";
      toast.error(msg);
    }
  };

  const loading =
    branchesLoading ||
    branchesFetching ||
    customersLoading ||
    customersFetching ||
    schemesLoading ||
    schemesFetching ||
    (isEdit && (enrollmentLoading || enrollmentFetching)) ||
    addState.isLoading ||
    updateState.isLoading ||
    isSubmitting;

  // react-select setters
 const setBranch = (opt) => {
  setValue("branchId", opt?.value || "", { shouldDirty: true });
  setValue("schemeId", "", { shouldDirty: true });
  setValue("customerId", "", { shouldDirty: true });
  setValue("schemeAmountPerMonth", "", { shouldDirty: true });
  setQuickAddedCustomer(null);
};

  const setScheme = (opt) => {
    setValue("schemeId", opt?.value || "", { shouldDirty: true });

    // Only set default if user hasn't typed/dirty
    const field = getFieldState("schemeAmountPerMonth");
    const currentVal = watch("schemeAmountPerMonth");
    if (field?.isDirty && currentVal !== "" && currentVal != null) return;

    const s = opt?.raw;
    if (s?.schemeAmountPerMonth != null) {
      setValue("schemeAmountPerMonth", String(s.schemeAmountPerMonth), {
        shouldDirty: false,
      });
    } else {
      setValue("schemeAmountPerMonth", "", { shouldDirty: false });
    }
  };

  const setCustomer = (opt) => {
    setValue("customerId", opt?.value || "", { shouldDirty: true });
  };

  const selectedBranchOpt = useMemo(
    () => branchOptions.find((o) => String(o.value) === String(branchId)),
    [branchOptions, branchId]
  );

  const selectedSchemeOpt = useMemo(
    () => schemeOptions.find((o) => String(o.value) === String(schemeId)),
    [schemeOptions, schemeId]
  );

  const selectedCustomerId = watch("customerId");

  useEffect(() => {
  if (!selectedCustomerId) return;

  const existsInBranch = filteredCustomers.some(
    (c) => String(c._id || c.id) === String(selectedCustomerId)
  );

  if (!existsInBranch) {
    setValue("customerId", "", { shouldDirty: true });
  }
}, [selectedCustomerId, filteredCustomers, setValue]);

  const selectedCustomerOpt = useMemo(
    () =>
      customerOptions.find(
        (o) => String(o.value) === String(selectedCustomerId)
      ),
    [customerOptions, selectedCustomerId]
  );

  return (
    <div className="">
      <QuickAddCustomerModal
        open={quickCustomerOpen}
        onClose={() => setQuickCustomerOpen(false)}
        selectedBranch={selectedBranchOpt || null}
        onAdded={async (created) => {
          const createdCustomer = Array.isArray(created) ? created[0] : created;
          const createdForBranch = {
            ...createdCustomer,
            branchId:
              createdCustomer?.branchId ||
              createdCustomer?.branch?._id ||
              selectedBranchOpt?.value ||
              "",
            branchName:
              createdCustomer?.branchName || selectedBranchOpt?.label || "",
          };
          setQuickAddedCustomer(createdForBranch);
          setValue(
            "customerId",
            createdForBranch?._id || createdForBranch?.id || "",
            {
              shouldDirty: true,
              shouldValidate: true,
            },
          );
          try {
            await refetchCustomers();
          } catch {
            // The created customer remains available from local state.
          }
          setQuickCustomerOpen(false);
          requestAnimationFrame(() => {
            setTimeout(() => customerSelectRef.current?.focus?.(), 80);
          });
        }}
      />
      {/* Header */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={"/gold-schemes/enrollments"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Enrollments"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>

          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
                  {isEdit
                    ? "Edit Gold Scheme Enrollment"
                    : "New Gold Scheme Enrollment"}
                </h1>
                <p className="text-xs text-gray-500">
                  Tips:{" "}
                  <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
                  <kbd className="px-1 bg-gray-100 rounded border">S</kbd> save
                  / next,{" "}
                  <kbd className="px-1 bg-gray-100 rounded border ml-1">
                    Ctrl
                  </kbd>
                  +
                  <kbd className="px-1 bg-gray-100 rounded border">
                    Back Spc
                  </kbd>{" "}
                  back,{" "}
                  <kbd className="px-1 bg-gray-100 rounded border ml-1">
                    Alt
                  </kbd>
                  +<kbd className="px-1 bg-gray-100 rounded border">←</kbd> prev
                  step
                </p>
              </div>

              <div className="hidden sm:block pr-2">
                <span className="text-xs text-gray-500">Step {step} of 2</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
      >
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form className="p-5" onSubmit={(e) => e.preventDefault()}>
          {/* STEP 1 */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Branch */}
              <div className="md:col-span-2">
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Branch<span className="text-red-500">*</span>
                </label>

                <Select
                  styles={withErrBorder(!!errors.branchId)}
                  value={selectedBranchOpt || null}
                  onChange={setBranch}
                  options={branchOptions}
                  placeholder="Select branch"
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  isDisabled={branchesLoading || branchesFetching}
                />

                <input
                  type="hidden"
                  {...register("branchId", { required: "Branch is required" })}
                />

                {errors.branchId && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.branchId.message}
                  </p>
                )}
              </div>

              {/* Scheme */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Scheme<span className="text-red-500">*</span>
                </label>

                <Select
                  styles={withErrBorder(!!errors.schemeId)}
                  value={selectedSchemeOpt || null}
                  onChange={setScheme}
                  options={schemeOptions}
                  placeholder={
                    branchForSchemeList
                      ? "Select scheme"
                      : "Select branch first"
                  }
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  isDisabled={
                    !branchForSchemeList || schemesLoading || schemesFetching
                  }
                />

                <input
                  type="hidden"
                  {...register("schemeId", { required: "Scheme is required" })}
                />

                {errors.schemeId && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.schemeId.message}
                  </p>
                )}

                {selectedScheme && (
                  <div className="mt-3 rounded-xl bg-[#9481ce]/10 border border-[#9481ce]/20 px-3 py-2 text-[13px] text-slate-700">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-[#4b3d91]">
                        {selectedScheme.schemeName}
                      </span>

                      <div className="flex items-center gap-2">
                        {selectedScheme.schemeType && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] bg-[#9481ce]/20 text-[#4b3d91] font-semibold">
                            {selectedScheme.schemeType}
                          </span>
                        )}
                        {selectedScheme.schemeType === "GRAM" &&
                          selectedScheme.metalType && (
                            <span className="px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-800 font-semibold border border-amber-200">
                              {selectedScheme.metalType}
                            </span>
                          )}
                      </div>
                    </div>

                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
                      <span>
                        Installment:{" "}
                        <strong>{selectedScheme.installmentMonths || 0}</strong>
                      </span>

                      {selectedScheme.schemeType === "AMOUNT" && (
                        <span>
                          Benefit:{" "}
                          <strong>
                            {selectedScheme.benefitsInMonths || 0}
                          </strong>
                        </span>
                      )}

                      {!!maturityMonths && (
                        <span>
                          Maturity: <strong>{maturityMonths}</strong> months
                        </span>
                      )}
                    </div>

                    {!!selectedScheme.schemeAmountPerMonth && (
                      <div className="mt-1 text-[12px]">
                        Minimum per month:{" "}
                        <strong>
                          ₹{" "}
                          {Number(selectedScheme.schemeAmountPerMonth).toFixed(
                            2
                          )}
                        </strong>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Customer */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Customer<span className="text-red-500">*</span>
                </label>

                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <Select
                      ref={customerSelectRef}
                      styles={withErrBorder(!!errors.customerId)}
                      value={selectedCustomerOpt || null}
                      onChange={setCustomer}
                      options={customerOptions}
                      placeholder={
                        branchForSchemeList
                          ? "Select customer"
                          : "Select branch first"
                      }
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                      isDisabled={
                        !branchForSchemeList ||
                        customersLoading ||
                        customersFetching
                      }
                    />
                  </div>

                  {!isEdit && (
                    <button
                      type="button"
                      onClick={() => {
                        if (!selectedBranchOpt?.value) {
                          toast.error("Select branch first.");
                          return;
                        }
                        setQuickCustomerOpen(true);
                      }}
                      className="h-11 shrink-0 rounded-lg border px-4 text-sm font-semibold text-[#6b59c6] hover:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-[#9481ce]"
                      title="Quick add customer"
                      aria-label="Quick add customer"
                    >
                      +
                    </button>
                  )}

                </div>

                <input
                  type="hidden"
                  {...register("customerId", {
                    required: "Customer is required",
                  })}
                />

                {errors.customerId && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.customerId.message}
                  </p>
                )}

                {selectedCustomerOpt?.raw && (
                  <div className="mt-3 rounded-xl bg-emerald-50/70 border border-emerald-200 px-3 py-2 text-[13px] text-slate-700">
                    <div className="font-semibold text-emerald-800">
                      {selectedCustomerOpt.raw.customerName ||
                        selectedCustomerOpt.raw.name}
                    </div>
                    <div className="text-[12px] text-emerald-700/80">
                      {selectedCustomerOpt.raw.mobile ||
                        selectedCustomerOpt.raw.phone ||
                        ""}
                    </div>
                    {selectedCustomerOpt.raw.customerCode && (
                      <div className="text-[11px] text-emerald-700/70 mt-1">
                        Code: {selectedCustomerOpt.raw.customerCode}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Start Date */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Start Date<span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  {...register("startDate", {
                    required: "Start date is required",
                  })}
                  className={`${baseInput} ${
                    errors.startDate ? errRing : okRing
                  }`}
                />
                {errors.startDate && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.startDate.message}
                  </p>
                )}

                {maturityPreviewDate && (
                  <p className="mt-1 text-xs text-gray-500">
                    Expected maturity:{" "}
                    <strong>
                      {maturityPreviewDate.toISOString().slice(0, 10)}
                    </strong>
                  </p>
                )}
              </div>

              {/* Amount per Month */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Amount per Month (₹)<span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min={1}
                  placeholder={
                    selectedScheme?.schemeAmountPerMonth
                      ? `Min ₹ ${Number(
                          selectedScheme.schemeAmountPerMonth
                        ).toFixed(2)} or more`
                      : "Enter amount per month"
                  }
                  {...register("schemeAmountPerMonth", {
                    validate: amountValidate,
                  })}
                  className={`${baseInput} ${
                    errors.schemeAmountPerMonth ? errRing : okRing
                  }`}
                />
                {errors.schemeAmountPerMonth && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.schemeAmountPerMonth.message}
                  </p>
                )}

                {!!selectedScheme?.schemeAmountPerMonth && (
                  <p className="mt-1 text-xs text-gray-500">
                    Minimum is{" "}
                    <strong>
                      ₹ {Number(selectedScheme.schemeAmountPerMonth).toFixed(2)}
                    </strong>
                    .
                  </p>
                )}
              </div>

              {/* Enrollment Code */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Enrollment Code (optional)
                </label>
                <input
                  type="text"
                  placeholder="Auto or manual code"
                  {...register("enrollmentCode")}
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* Remarks */}
              <div className="md:col-span-2">
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Remarks / Notes
                </label>
                <textarea
                  rows={3}
                  placeholder="Any special instruction for this enrollment…"
                  {...register("remarks")}
                  className={`${baseInput} ${okRing} resize-y`}
                />
              </div>
            </div>
          )}

          {/* Footer actions */}
          <div className="flex flex-col sm:flex-row gap-2 pt-5">
            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="button"
              onClick={prevStep}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-60"
              disabled={step === 1 || loading}
              title="Previous step (Alt+←)"
            >
              Previous
            </motion.button>

            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (step < 2) nextStep();
                else handleSubmit(onSubmit)();
              }}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
              disabled={loading}
              title={step === 2 ? "Submit (Ctrl+S)" : "Next (Ctrl+S)"}
            >
              {loading
                ? "Saving..."
                : step === 2
                ? isEdit
                  ? "Update (Ctrl+S)"
                  : "Submit (Ctrl+S)"
                : "Next"}
            </motion.button>

            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="button"
              onClick={() => {
                const keepBranch =
                  watch("branchId") || authUser?.branchId || "";
                reset({ ...DEFAULTS, branchId: keepBranch });
                setStep(1);
                requestAnimationFrame(() => setFocus("branchId"));
              }}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-60"
              disabled={loading}
              title="Clear form"
            >
              Clear
            </motion.button>

            <Link
              to="/gold-schemes/enrollments"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default GoldSchemeEnrollmentForm;
